/**
 * Translate the React quote-form payload into the flat `customer_*`/`pool_*`
 * shape that cabana-lead-processor expects.
 *
 * Mirrors the field mapping the WP plugin (`Pool Service Request`) used
 * when it built its Zapier webhook payload, so the lead-processor sees the
 * same input regardless of whether the form is hitting the WP plugin or
 * this Vercel proxy.
 */

const VALUE_LABELS = {
  residential: 'Residential',
  commercial: 'Commercial',
  regular_pool_spa_service: 'Regular pool or spa service',
  equipment_repair: 'Equipment upgrade or repair',
  filter_or_salt_cell_cleaning: 'Filter or salt cell cleaning',
  greentoclean: '"Green to clean" pool rescue',
  something_else: 'Something else',
  pool_pump: 'Pool pump',
  pool_filter: 'Pool filter',
  pool_heater: 'Pool heater',
  salt_system: 'Salt system',
  automation_system: 'Automation system',
  i_m_not_sure: 'I am not sure',
  pool_only: 'Pool only',
  pool_spa: 'Pool + attached spa',
  pool_hot_tub: 'Pool + separate hot tub',
  hot_tub: 'Above ground hot tub',
  ground_pool: 'I have an above ground pool',
  saltwater_pool: 'I have a saltwater pool',
  trees_over_pool: 'I have trees over the pool',
  // Pool-condition values include their position on the 1-to-5 scale so a
  // sales rep reading the deal note immediately knows the severity. 1 is
  // clearest, 5 is the worst (swamp). Anything 4 or 5 needs green-to-clean
  // before regular service can start.
  crystal_clear: '1 of 5 - Crystal clear',
  slightly_cloudy: '2 of 5 - Slightly cloudy',
  cloudy_light_green: '3 of 5 - Cloudy and light green',
  green: '4 of 5 - Green',
  swamp: '5 of 5 - Swamp',
};

function pickFirst(data, keys) {
  for (const k of keys) {
    const v = data[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return '';
}

function labelize(value) {
  if (Array.isArray(value)) {
    return value.map((v) => VALUE_LABELS[v] || v).join(', ');
  }
  if (typeof value === 'string' && value in VALUE_LABELS) {
    return VALUE_LABELS[value];
  }
  return value || '';
}

function digitsOnly(s) {
  return String(s || '').replace(/\D+/g, '');
}

function formatPhoneE164(raw) {
  const d = digitsOnly(raw);
  if (d.length === 10) return '+1' + d;
  if (d.length === 11 && d.startsWith('1')) return '+' + d;
  if (d.length === 0) return '';
  return '+' + d;
}

function buildAddress(data) {
  if (data.address) return data.address;
  const parts = [data.street, data.city, data.state, data.zipCode]
    .filter(Boolean);
  return parts.join(', ');
}

function buildName(data) {
  if (data.personName) return data.personName;
  const fn = pickFirst(data, [
    'commercialFirstName',
    'estimateFirstName',
    'personFirstName',
    'firstName',
  ]);
  const ln = pickFirst(data, [
    'commercialLastName',
    'estimateLastName',
    'personLastName',
    'lastName',
  ]);
  return [fn, ln].filter(v => typeof v === 'string').join(' ').trim();
}

export function transformLeadPayload(data, siteOrigin) {
  const phoneRaw = pickFirst(data, ['estimatePhone', 'personPhone', 'phone']);
  const phoneE164 = formatPhoneE164(phoneRaw);

  return {
    request_type: 'Pool Service Request',
    site_origin: siteOrigin,
    customer_name: buildName(data),
    customer_email: pickFirst(data, [
      'commercialEmail',
      'estimateEmail',
      'personEmail',
      'email',
    ]),
    customer_phone: phoneE164,
    contact_id: data.contactId || '',
    contactId: data.contactId || '',
    address: buildAddress(data),
    location_coordinates: data.location || '',
    zip_code: data.zipCode || '',
    pool_type: labelize(data.poolType),
    service_needed: labelize(data.service),
    service_something_else: data.serviceSomethingElse || '',
    pool_features: labelize(data.poolOption),
    pool_environment: labelize(data.poolOption1),
    pool_size: data.poolSize || '',
    pool_condition: labelize(data.poolCondition),
    // Reference photo the customer matched their pool against on the slider.
    // Reps click through to see exactly what condition the customer
    // self-reported (URL serves from quote.cabanapools.com production).
    pool_condition_reference_url: typeof data.poolConditionIndex === 'number'
      && data.poolCondition
      ? `https://quote.cabanapools.com/images/pool-condition/pool-condition-${data.poolConditionIndex + 1}-${data.poolCondition.replace(/_/g, '-')}.jpg`
      : '',
    pool_image_url: data.PoolURL || data.poolURL || '',
    company_name: data.commercialCompanyName || '',
    need_help: data.commercialHelp || '',
    pool_help: labelize(data.poolHelp),
    resume_token: typeof data.resumeToken === 'string' ? data.resumeToken : '',
    // Capture answers in the client's own keys for cross-device restoration.
    resume_snapshot: Object.fromEntries([
      'personName', 'phone', 'address', 'zipCode', 'location', 'poolType', 'service',
      'serviceSomethingElse', 'poolHelp', 'poolHelpNotSure', 'poolOption', 'poolOption1',
      'poolSize', 'poolSize2', 'poolSizeSq', 'poolCondition', 'poolConditionIndex',
      'email', 'personEmail', 'estimateEmail', 'estimatePhone', 'estimateFirstName',
      'estimateLastName', 'contactId', 'statsig_stable_id', 'split_assignment_id',
    ].filter(key => data[key] !== undefined).map(key => [key, data[key]])),
    // attribution
    utm_source: data.utm_source || '',
    utm_medium: data.utm_medium || '',
    utm_device: data.utm_device || '',
    utm_campaign: data.utm_campaign || '',
    utm_term: data.utm_term || '',
    utm_content: data.utm_content || '',
    gclid: data.gclid || '',
    msclkid: data.msclkid || '',
    wbraid: data.wbraid || '',
    gbraid: data.gbraid || '',
    fbclid: data.fbclid || '',
    oppref: data.oppref || '',
    promo: data.promo || '',
    referrer: data.referrer || '',
    landing_page: data.landing_page || '',
    user_agent: data.user_agent || '',
    // Meta CAPI session signals: capture EMQ-boosting match keys at submit
    // time so the offline-conversions Cloud Function can stash them in
    // Firestore (meta_session_keys/{deal_id}) and re-attach to later
    // Lead/Quote/Purchase events fired from Sell stage changes.
    meta_fbp: data.meta_fbp || '',
    meta_fbc: data.meta_fbc || '',
    client_user_agent: data.client_user_agent || data.user_agent || '',
    client_ip_address: data.client_ip_address || '',
    // Bot defense signals. turnstile_token is verified server-side in
    // /api/lead-submit before this transform is called, so we don't forward
    // it downstream. FingerprintJS Pro was removed on 2026-07-17, so the
    // fingerprint_visitor_id / fingerprint_request_id keys are gone; the
    // comment that used to live here claimed offline-conversions consumed
    // the visitor id as a Meta CAPI external_id, which was never true
    // (external_id is sha256(deal_id)). Downstream readers all use
    // .get(...) or "" and degrade to empty on the missing keys.
    ga4_id: data.ga4_id ? String(data.ga4_id) : '',
    ga_session_id: data.ga_session_id || '',
    gpc_signal: Boolean(data.gpc_signal),
    statsig_user_id: data.statsig_user_id || '',
    statsig_stable_id: data.statsig_stable_id || '',
    quote_session: typeof data.quote_session === 'string' ? data.quote_session : '',
    submission_contract: data.submission_contract,
    submission_id: data.submission_id,
    submission_revision: data.submission_revision,
    quote_variant: ['control', 'treatment', 'split_direct'].includes(data.quote_variant) ? data.quote_variant : '',
    quote_experiment_name: typeof data.quote_experiment_name === 'string' ? data.quote_experiment_name.slice(0, 128) : '',
    service_option: data.serviceOption && typeof data.serviceOption === 'object'
      ? {
        offerId: data.serviceOption.offerId,
        serviceId: data.serviceOption.serviceId,
        selectedExtras: data.serviceOption.selectedExtras || {},
      } : null,
    // Contracted callers must supply exact intent. Preserve malformed values
    // for backend rejection instead of turning the string "false" into true.
    autoSubmitted: data.submission_contract === 1 ? data.autoSubmitted : Boolean(data.autoSubmitted),
    autoSubmitReason: data.autoSubmitReason === 'page_hide' ? 'page_hidden' : ['browser_close', 'page_hidden', 'inactivity_timeout'].includes(
      data.autoSubmitReason
    )
      ? data.autoSubmitReason
      : '',
    has_filter_cleaning: data.has_filter_cleaning || 'No',
    has_pool_rx: data.has_pool_rx || 'No',
    has_heater_tuneup: data.has_heater_tuneup || 'No',
    // The lead processor tags the Sell deal "Salt System" from this flag, and
    // the quote publisher reads that tag into hasSaltSystem and modifiers.
    has_salt_system: data.has_salt_system === 'Yes' ? 'Yes' : 'No',
    selected_services: Array.isArray(data.selectedServices)
      ? data.selectedServices
      : [],
    quote_amount: typeof data.amount === 'number' ? data.amount : parseFloat(data.amount) || 0,
    self_serve_quote_accepted: Boolean(data.selfServeQuoteAccepted),
    form_version: data.formVersion || '',
    // Direct vs Cabana split test. The processor never trusts these: it
    // re-reads the assignment from Firestore and checks it belongs to this
    // statsig_stable_id before routing.
    split_assignment_id: typeof data.split_assignment_id === 'string'
      ? data.split_assignment_id.trim().slice(0, 128) : '',
    split_direct_request: data.split_direct_request === true,
    // QA override token on a direct-arm submit; empty when no override.
    split_qa_token: typeof data.split_qa_token === 'string'
      ? data.split_qa_token.trim().slice(0, 128) : '',
    // Plan labels the customer ticked on the direct-arm short form, for the
    // brand intake email ("Weekly plans I'm interested in"). Labels only.
    split_plan_interest: Array.isArray(data.split_plan_interest)
      ? data.split_plan_interest.filter(v => typeof v === 'string' && v.trim())
        .slice(0, 8).map(v => v.trim().slice(0, 120))
      : [],
  };
}
